import React from 'react';
import PropTypes from 'prop-types';
import MetaControl from 'Components/UI/Meta/MetaControl';
import fr from 'facet-rest';
import useHomeContext from 'Context/HomeContext';

const relationshipMetaOverrideSet = fr.metaOverride({}).nameOverride('relationship', { required: true });

const shouldHaveEmailAddr = (client) => client.attr('relationship') === 'spouse' || client.attr('relationship') === 'significantOther' || client.attr('isPrimary') === 'true';

function createLoginEmailAddress(client) {
  if (!shouldHaveEmailAddr(client)) return undefined;
  const addr = client.attr('emailAddresses').create();
  addr.attr('type', 'login');
  addr.attr('isPreferred', 'true');
  addr.attr('isPrimary', 'true');
  if (client.id()) {
    addr.attr('clientId', `${client.id()}`);
  }
  client
    .attr('emailAddresses')
    .resources()
    .pushItem(null, addr);
  return addr;
}

export default function Personal({ handleUpdate, client }) {
  const { homeResource } = useHomeContext();
  const emailAddrs = client
    .attr('emailAddresses')
    .resources()
    .list();
  const loginEmailAddress = emailAddrs.find(email => email.attr('type') === 'login')
      ?? createLoginEmailAddress(client); // Handles if you just created a brand new spouse record, and handles if you already had a spouse before they needed to have a login email and they're missing it
  return (
    <>
      {client.attr('isPrimary') !== 'true' && (
        <MetaControl
          attrName="relationship"
          onChange={value => {
            client.attr('relationship', value);
            if (!shouldHaveEmailAddr(client)) {
              // If you switch from spouse to child (ew) then clear the email address, so you don't get an error about email being required
              client.attr('emailAddresses').resources().removeAllItems();
            }
            handleUpdate(client);
          }}
          resource={client}
          metaOverrideSet={relationshipMetaOverrideSet}
          test={'client-card-personal-first-name-input'}
        />
      )}
      <MetaControl
        attrName="firstName"
        onChange={value => handleUpdate(client.attr('firstName', value))}
        resource={client}
      />
      <MetaControl
        attrName="lastName"
        onChange={value => handleUpdate(client.attr('lastName', value))}
        resource={client}
      />
      {client.type() === 'client' && (
        <MetaControl
          attrName="nickname"
          onChange={value => handleUpdate(client.attr('nickname', value))}
          resource={client}
        />
      )}
      <MetaControl
        attrName="birthDate"
        openTo="year" // TODO: Make meta override. Do not duplicate pattern.
        onChange={value => handleUpdate(client.attr('birthDate', value))}
        resource={client}
      />
      {homeResource.withRelationship(
        fr.relationships.onlyLoginEmailAddress.name,
        () => shouldHaveEmailAddr(client) && (
        <MetaControl
          attrName="address"
          onChange={(value) => {
            // In the past, users could enter multiple email addresses of different types.
            // If someone already chose an address like a@b.com but used a non-login type, we no longer allow them to delete that old a@b.com email address.
            // And you can't have an email address duplicate, so if a user has a login email of test@b.com and wants to change it to a@b.com (a non-login email), the server prevents that.
            // To get around this, if the user tries to change their login address to a@b.com, remove the old a@b.com non-login email address before adding in the a@b.com login address
            const identicalEmailAddrIdx = emailAddrs.findIndex((email) => email.attr('type') !== 'login' && email.attr('address') === value);
            if (identicalEmailAddrIdx !== -1) {
              client.attr('emailAddresses').resources().removeItemByIndex(identicalEmailAddrIdx);
            }

            // Actually update the email address + client
            loginEmailAddress.attr('address', value);
            handleUpdate(client);
          }}
          resource={loginEmailAddress}
        />
        ),
        () => null,
      )}
      {client.attr('relationship') === 'spouse' && (
        <MetaControl
          attrName="anniversaryDate"
          openTo="year"
          onChange={value => handleUpdate(client.attr('anniversaryDate', value))}
          resource={client}
        />
      )}
    </>
  );
}

Personal.propTypes = {
  handleUpdate: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
};
